/* eslint-disable */
import { all, call, put, take, select } from "redux-saga/effects";
import { replace, push } from "react-router-redux";
import { isEqual, get } from "lodash";
import { dispatchSignIn, api, showError, showEmailVerification, checkForAccess } from "rp-auth";
import trackingSaga, { trackSubscriptionSaleAttemptSaga } from "./tracking";
import fetchUserInfo from "./fetchUserInfo";
import fetchMppOffer from "./fetchMppOffer";
import fetchMppOfferVouchers from "./fetchMppOfferVouchers";
import fetchUserServices from "./fetchUserServices";
import fetchSessionStatus from "./fetchSessionStatus";
import editUserInfo from "./editUserInfo";
import { watchRenewSubscription } from "./renewSubscription";
import watchAddNewCard from "./watchAddNewCard";
import watchEditUserInfo from "./watchEditUserInfo";
import watchSubscriptionConfirm from "./watchSubscriptionConfirm";
import { watchLoadEmailConfirmationPage } from "../components/EmailConfirmationPage/sagas";
import { watchUpgradeSubscription } from "./upgradeSubscription";
import {
    OFFER_INFO_LOAD_REQUESTED,
    OFFER_INFO_LOAD_AFTER_LOGIN_REQUESTED,
    loadOfferVoucherInfoRequest,
} from '../actions/campaignOfferInfo';
import { ACCOUNT_PAGE_LOAD_REQUESTED } from "../components/AccountPage/actions";
import { SKIP_LOGIN_STEP, GO_TO_NEXT_STEP_WHEN_LOGIN_IS_SKIPPED } from "../actions/auth";
import {
    PAYMENT_PAGE_LOAD_REQUESTED,
    PAYMENT_PAGE_LOAD_REQUESTED_AFTER_LOGIN,
} from "../components/PaymentPage/actions";
import {
    authenticateUserSuccess,
    authenticateUserFailure,
    loadUserAmendFailure,
    createUserSuccess,
    createUserFailure,
    USER_AMEND_LOAD_REQUESTED,
    USER_CREATE_LOAD_REQUESTED,
    USER_AUTHENTICATE_LOAD_REQUESTED,
    loadUserInfoFailure
} from "../actions/userInfo";
import { loadAccountPageSuccess, loadAccountPageFailure } from "../components/AccountPage/actions";
import { loadPaymentPageFailure } from "../components/PaymentPage/actions";
import { goToNextStep } from "../components/PaymentPage/PaymentWizard/actions";
import {
    identifySignUp,
    trackLogin,
    trackRegister,
    subscriptionStepCompleted,
    detailsViewed,
    detailsCompleted
} from "../actions/tracking";

import { subscriptionPick } from "../actions/subscriptions";
import { getActiveSubscription, subscriptionsOptionsSelector, getSubscriptionVoucher } from "../selectors";
import { brazeEmailSubscription } from "./brazeEmailSubscription";

const { AuthSignIn, AuthSignUp } = api;

const ERROR_OH_NO = {
    errorMessage: "API Authentication Failure: Invalid Authentication Type/Credentials Provided"
};

function* hasActiveSubscription() {
    return Boolean(yield select(getActiveSubscription));
}

export function* getSession() {
    const isBarrierActive = global.location.href.indexOf(`?authme`) === -1;
    try {
        const { sessionStatus, session } = yield call(fetchSessionStatus);
        const isHighSecurityInactive = !sessionStatus || !sessionStatus.shortSessionActive;

        return { isHighSecurityInactive, isBarrierActive, sessionStatus, session };
    } catch (error) {
        return {
            isHighSecurityInactive: true,
            isBarrierActive,
            sessionStatus: false,
            session: false
        };
    }
}

export function* watchLoadUserInfo() {
    const { sessionStatus, session } = yield call(getSession);

    if (session) {
        yield call(fetchUserInfo, sessionStatus.accountReference, session);
    } else {
        yield put(loadUserInfoFailure());
    }
}

export function* watchLoadAccountPage() {
    while (true) {
        yield take(ACCOUNT_PAGE_LOAD_REQUESTED);

        try {
            const { sessionStatus, session, isHighSecurityInactive, isBarrierActive } = yield call(
                getSession
            );

            if (isHighSecurityInactive) {
                yield put(loadAccountPageFailure());
                if (isBarrierActive) {
                    checkForAccess("SignIn", true, false, { r: global.location.href });
                }
                continue;
            }

            const [userInfo] = yield all([
                yield call(fetchUserInfo, sessionStatus.accountReference, session),
                yield call(fetchUserServices, sessionStatus.accountReference, session)
            ]);

            if (!userInfo) {
                yield put(loadAccountPageFailure());
            } else {
                yield put(loadAccountPageSuccess());
            }
        } catch (error) {
            yield put(loadAccountPageFailure());
        }
    }
}

// campaign offer watcher
export function* watchLoadCampaignPaymentPageAfterLogin() {
    while (true) {
        try {
            let action = yield take(OFFER_INFO_LOAD_AFTER_LOGIN_REQUESTED);
            const { sessionStatus, session, isHighSecurityInactive } = yield call(getSession);

            // Checking for if short session expired
            if (isHighSecurityInactive) {
                yield put(push("/subscriptions/"));
                continue;
            }

            let userInfo = yield call(fetchUserInfo, sessionStatus.accountReference, session);

            if (!userInfo) {
                yield put(push("/subscriptions/"));
            } else {
                // Making sure the selected option is valid
                const options = yield select(subscriptionsOptionsSelector);
                const selectedOption = options.find(
                    item => item.id === action.payload.id
                );

                // If user tried manually to do things
                // Which he shouldn't
                if (!selectedOption) {
                    yield put(loadPaymentPageFailure());
                    yield put(push("/subscriptions/"));
                    continue;
                }

                // Setting selected subscription
                yield put(authenticateUserSuccess(userInfo));
                yield put(subscriptionStepCompleted(userInfo.sessionToken));

                // Getting user subs
                yield call(fetchUserServices, userInfo.accountReference, userInfo.sessionToken);

                // If user has active subs
                // Redirecting straight to my-account
                if (yield select(getActiveSubscription)) {
                    yield put(push("/subscriptions/my-account/#tab=subscriptions"));
                    continue;
                }

                yield put(subscriptionPick(action.payload));
                // Moving to details step
                yield put(goToNextStep());
                yield put(detailsViewed(userInfo.sessionToken));

                // Waiting for completion of details step
                action = yield take(USER_AMEND_LOAD_REQUESTED);

                // Updating user's details
                userInfo = yield call(editUserInfo, action);

                // If successful go to next step which is payment
                if (userInfo) {
                    yield put(identifySignUp(userInfo));
                    yield put(detailsCompleted(userInfo.sessionToken));
                    yield put(goToNextStep());
                }
            }
        } catch (error) {
            yield put(loadPaymentPageFailure());
        }
    }
}

export function* watchLoadPaymentPageAfterLogin() {
    while (true) {
        // Waiting for the action to be executed with this type
        let action = yield take(PAYMENT_PAGE_LOAD_REQUESTED_AFTER_LOGIN);
        try {
            const { sessionStatus, session, isHighSecurityInactive } = yield call(getSession);

            // Checking for if short session expired
            if (isHighSecurityInactive) {
                yield put(loadPaymentPageFailure());
                yield put(push("/subscriptions/"));
                continue;
            }

            let userInfo = yield call(fetchUserInfo, sessionStatus.accountReference, session);

            if (!userInfo) {
                yield put(loadPaymentPageFailure());
                yield put(push("/subscriptions/"));
            } else {
                // Making sure the selected option is valid
                const options = yield select(subscriptionsOptionsSelector);
                const selectedOption = options.find(
                    item => item.id === action.payload.selectedState
                );

                // If user tried manually to do things
                // Which he shouldn't
                if (!selectedOption) {
                    yield put(loadPaymentPageFailure());
                    yield put(push("/subscriptions/"));
                    continue;
                }

                // Setting selected subscription
                yield put(authenticateUserSuccess(userInfo));
                yield put(subscriptionStepCompleted(userInfo.sessionToken));

                // Getting user subs
                yield call(fetchUserServices, userInfo.accountReference, userInfo.sessionToken);

                // If user has active subs
                // Redirecting straight to my-account
                if (yield select(getActiveSubscription)) {
                    yield put(push("/subscriptions/my-account/#tab=subscriptions"));
                    continue;
                }

                yield put(subscriptionPick(selectedOption));
                // Moving to details step
                yield put(goToNextStep());
                yield put(detailsViewed(userInfo.sessionToken));

                // Waiting for completion of details step
                action = yield take(USER_AMEND_LOAD_REQUESTED);

                // Updating user's details
                userInfo = yield call(editUserInfo, action);

                // If successful go to next step which is payment
                if (userInfo) {
                    yield put(identifySignUp(userInfo));
                    yield put(detailsCompleted(userInfo.sessionToken));
                    yield put(goToNextStep());
                }
            }
        } catch (error) {
            yield put(loadPaymentPageFailure());
        }
    }
}

export function* watchLoadPaymentPage() {
    while (true) {
        yield take(PAYMENT_PAGE_LOAD_REQUESTED);

        try {
            const { sessionStatus, session } = yield call(getSession);
            let userInfo = yield call(fetchUserInfo, sessionStatus.accountReference, session);

            if (!session || !sessionStatus.longSessionActive) {
                yield put(loadPaymentPageFailure());
            }

            if (sessionStatus.longSessionActive && !sessionStatus.shortSessionActive) {
                // if shortSession is not active but flow is triggered from campaign url, no need to redicrect to login
                if(yield select(getSubscriptionVoucher)) {
                    return;
                };
                // we want to redirect it to my-account and it will display the login modal
                yield put(push("/subscriptions/my-account/#tab=subscriptions"));
            }

            if (!userInfo) {
                yield put(loadPaymentPageFailure());
            } else {
                // Getting user subs
                yield put({ type: SKIP_LOGIN_STEP });
                yield call(fetchUserServices, userInfo.accountReference, userInfo.sessionToken);
                // If user has active subs
                // Redirecting straight to my-account
                if (yield select(getActiveSubscription)) {
                    yield put(push("/subscriptions/my-account/#tab=subscriptions"));
                }

                yield put(authenticateUserSuccess(userInfo));
                yield put(detailsCompleted(userInfo.sessionToken));
                yield put(goToNextStep());
            }
        } catch (error) {
            yield put(loadPaymentPageFailure());
        }
    }
}

export function* watchCampaignRequest() {
    while (true) {
        const { payload: { subscriptionReference, offerReference, voucherCode, getSubscriptionSelectedData } } = yield take(OFFER_INFO_LOAD_REQUESTED);

        try {
            const { sessionStatus, session } = yield call(getSession);
            let userInfo = yield call(fetchUserInfo, sessionStatus.accountReference, session);

            // call mpp offers api endpoint
            const offerApiData = yield call(fetchMppOffer, subscriptionReference, offerReference, session, getSubscriptionSelectedData);

            if(!offerApiData){
                throw new Error('Offer cannot be found.');
            }

            const offerData = {
                offerReference,
                session,
                getVouchers: true,
                voucherCode,
            }

            yield put(loadOfferVoucherInfoRequest(offerData));

            const offerApiVoucherData = yield call(fetchMppOfferVouchers, offerReference, session, true, voucherCode);

            if(!offerApiVoucherData){
                throw new Error('Voucher cannot be found.');
            }

            offerApiData.selectedModOffer['voucherCode'] = voucherCode;

            yield put(subscriptionPick(offerApiData.selectedModOffer));

            if (userInfo) {
                // Getting user subs
                yield put({ type: SKIP_LOGIN_STEP });
                yield call(fetchUserServices, userInfo.accountReference, userInfo.sessionToken);

                // If user has active subs, redirecting straight to my-account
                if (yield select(getActiveSubscription)) {
                    yield put(push("/subscriptions/my-account/#tab=subscriptions"));
                }

                yield put(authenticateUserSuccess(userInfo));
            }
        } catch (error) {
            console.error('Subscription fetch offer error: ', error); // eslint-disable-line
            yield put(push("/subscriptions"));
            yield put(loadPaymentPageFailure());
        }
    }
}

export function* goToNextStepWhenLoginIsSkipped() {
    yield take(GO_TO_NEXT_STEP_WHEN_LOGIN_IS_SKIPPED);

    yield put(goToNextStep());
}

export function* watchCreateUser() {
    while (true) {
        try {
            let action = yield take(USER_CREATE_LOAD_REQUESTED);
            let userInfo = yield call(AuthSignUp, action.payload);
            yield put(identifySignUp(userInfo));

            yield call(brazeEmailSubscription, userInfo);

            yield put(trackRegister(userInfo));
            yield put(createUserSuccess(userInfo));
            yield put(subscriptionStepCompleted(userInfo.sessionToken));
            yield put(goToNextStep());
            yield put(detailsViewed(userInfo.sessionToken));

            action = yield take(USER_AMEND_LOAD_REQUESTED);
            userInfo = yield call(editUserInfo, action, {
                sessionStatus: { accountReference: userInfo.accountReference },
                session: userInfo.sessionToken
            });

            if (userInfo) {
                yield put(identifySignUp(userInfo));
                yield put(detailsCompleted(userInfo.sessionToken));
                yield call(trackSubscriptionSaleAttemptSaga, userInfo);
                yield put(goToNextStep({ sessionId: userInfo.sessionToken }));
            }
        } catch (error) {
            yield put(createUserFailure(error.response.data));

            if (isEqual(error.response.data, ERROR_OH_NO)) {
                yield call(showError);
            }
        }
    }
}

export function* watchAuthenticateUser() {
    while (true) {
        let action = yield take(USER_AUTHENTICATE_LOAD_REQUESTED);

        try {
            yield call(AuthSignIn, action.payload.email, action.payload.password, false);
            yield call(dispatchSignIn);

            let userInfo = yield call(editUserInfo, {
                payload: { conditions: { ...action.payload.conditions } }
            });
            yield put(trackLogin(userInfo));

            yield put(authenticateUserSuccess(userInfo));
            yield put(subscriptionStepCompleted(userInfo.sessionToken));

            yield call(fetchUserServices, userInfo.accountReference, userInfo.sessionToken);

            if (yield hasActiveSubscription()) {
                yield put(replace("/subscriptions/my-account/#tab=subscriptions"));
                continue;
            }

            yield put(goToNextStep());
            yield put(detailsViewed(userInfo.sessionToken));

            action = yield take(USER_AMEND_LOAD_REQUESTED);
            userInfo = yield call(editUserInfo, action);

            if (userInfo) {
                yield put(identifySignUp(userInfo));
                yield put(detailsCompleted(userInfo.sessionToken));
                yield put(goToNextStep());
            }
        } catch (error) {
            if (error.accountNotVerified) {
                yield call(showEmailVerification, action.payload.email, error.accountId);
            } else if (get(error, "response.data", "")) {
                if (isEqual(error.response.data, ERROR_OH_NO)) {
                    yield call(showError);
                }

                yield put(loadUserAmendFailure(error.response.data));
            }
            yield put(authenticateUserFailure());
        }
    }
}

function* rootSaga() {
    yield all([
        call(watchAddNewCard),
        call(watchAuthenticateUser),
        call(watchSubscriptionConfirm),
        call(watchLoadAccountPage),
        call(watchLoadPaymentPage),
        call(watchLoadEmailConfirmationPage),
        call(watchEditUserInfo),
        call(watchUpgradeSubscription),
        call(watchLoadUserInfo),
        call(watchRenewSubscription),
        call(watchCreateUser),
        call(trackingSaga),
        call(watchLoadPaymentPageAfterLogin),
        call(watchCampaignRequest),
        call(goToNextStepWhenLoginIsSkipped),
        call(watchLoadCampaignPaymentPageAfterLogin),
    ]);
}

export default rootSaga;
